import { useState, useMemo, useCallback, Dispatch, SetStateAction } from "react";
import clsx from "clsx";
// Materials
import { GridColDef, GridEventListener, GridFilterItem, GridFilterModel, GridRowId, GridRowParams } from "@mui/x-data-grid-premium";

// Components
import { ChangeRequestsTableHeader } from "./ChangeRequestTableHeader";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";

// Helpers
import { getBaseColumns, columnVisibilityModel, customProps, positionFilterItemId, slots } from "./constants";
import { buildDataWithSkeletonRows, changePositionFilterValue, wrapColumnDefinitions } from "./helpers";
// Types
import { IGetChangeRequest } from "@/Washouts/types/PayrollClosing/queries";
import { useLocation } from "@/Common/hooks/Location/useLocation";

const buildColumnsDefinition = (timezone: string) => {
	const columns: GridColDef<IGetChangeRequest>[] = getBaseColumns(timezone).map(wrapColumnDefinitions);
	return columns;
};

export type TChangesTableProps = {
	data: IGetChangeRequest[];
	isLoading: boolean;
	disableRowClick?: boolean;
	switchValue?: "PENDING" | "REJECTED" | "RESOLVED";
	initialSwitchState?: boolean;
	onSwitchChange?: (value: boolean) => void;
	showPositionsSelector?: boolean;
	skeletonRows?: string[];
	initialSelectedPositions?: string[];
	onSelectedPositionsChange?: (positions: string[]) => void;
	// Row Selection
	selectedRowId: string | null;
	setSelectedRowId: Dispatch<SetStateAction<string | null>>;
	allowDeletion?: boolean;
	onRowDeleted?: (rows: IGetChangeRequest[]) => boolean | Promise<boolean>;
	isRowSelectable?: (row: IGetChangeRequest) => boolean;
};

export const ChangeRequestTable = (props: Readonly<TChangesTableProps>) => {
	const {
		data,
		isLoading,
		initialSwitchState = true,
		switchValue: defaultSwitchValue,
		skeletonRows,
		onSwitchChange,
		disableRowClick,
		initialSelectedPositions = [],
		onSelectedPositionsChange,
		showPositionsSelector,
		allowDeletion = false,
		onRowDeleted,
		selectedRowId,
		setSelectedRowId,
		isRowSelectable = () => true,
	} = props;

	const { timezone } = useLocation();
	const columns = useMemo(() => buildColumnsDefinition(timezone), [timezone]);

	const [selectedPositions, setSelectedPositions] = useState<{ label: string }[]>(
		initialSelectedPositions.map((pos) => ({ label: pos }))
	);
	const switchFilterItem = useMemo<GridFilterItem>(
		() => ({ id: "initial-filter", field: "status", operator: "equals", value: defaultSwitchValue }),
		[]
	);
	const positionsFilterItem = useMemo<GridFilterItem>(
		() => ({ id: positionFilterItemId, field: "position", operator: "isAnyOf", value: [] }),
		[]
	);
	const initialFilterModel = useMemo(() => {
		const items: GridFilterItem[] = [];
		if (selectedPositions.length > 0) {
			items.push({ ...positionsFilterItem, value: selectedPositions.map((pos) => pos.label) });
		}
		if (initialSwitchState && defaultSwitchValue) {
			items.push(switchFilterItem);
		}
		return { items };
	}, []);
	const [deleting, setDeleting] = useState(false);
	const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);

	const [selectedRows, setSelectedRows] = useState<Readonly<GridRowId[]>>([]);

	const augmentedData = useMemo(() => buildDataWithSkeletonRows(data ?? [], skeletonRows), [data, skeletonRows]);

	const isSwitchFilterItem = useCallback(
		(item: GridFilterItem) =>
			item.field === switchFilterItem.field && item.value === switchFilterItem.value && item.operator === switchFilterItem.operator,
		[switchFilterItem]
	);

	const handlePositionsChange = useCallback((newPositions: { label: string }[]) => {
		setSelectedPositions(newPositions);
		if (newPositions.length === 0) {
			setFilterModel((currentModel) => ({
				...currentModel,
				items: currentModel.items.filter((item) => item.id !== positionsFilterItem.id),
			}));
			return;
		}

		setFilterModel((currentModel) => {
			let newItems = currentModel.items;
			if (!newItems.find((item) => item.id === positionsFilterItem.id)) {
				newItems = [positionsFilterItem, ...newItems];
			}

			return {
				...currentModel,
				items: changePositionFilterValue(newItems, newPositions),
			};
		});

		onSelectedPositionsChange?.(newPositions.map((pos) => pos.label));
	}, []);

	const handleRowClick = useCallback<GridEventListener<"rowClick">>(
		(params) => {
			if (!data || disableRowClick) {
				return;
			}
			setSelectedRowId(params.row._id);
			setSelectedRows([]);
		},
		[data, disableRowClick]
	);

	const handleSwitchChange = useCallback((value: boolean) => {
		if (value) {
			setFilterModel((currentModel) => ({
				...currentModel,
				items: [switchFilterItem, ...currentModel.items.filter((item) => item.id !== switchFilterItem.id)],
			}));
		} else {
			setFilterModel((currentModel) => ({
				...currentModel,
				items: currentModel.items.filter((item) => !isSwitchFilterItem(item)),
			}));
		}
		onSwitchChange?.(value);
	}, []);

	const checked = !!filterModel.items.find(isSwitchFilterItem);

	const initialState = useMemo(() => ({ filter: { filterModel } }), []);

	const positions = useMemo(
		() => Array.from(new Set(data?.map((item) => item.position))).map((position) => ({ label: position })),
		[data]
	);

	const handleDeleteAction = useCallback(async () => {
		setDeleting(true);
		const resolved = await onRowDeleted?.(selectedRows.map((id) => data?.find((row) => row._id === id) as IGetChangeRequest));
		if (resolved) {
			setSelectedRows([]);
		}
		setDeleting(false);
	}, [selectedRows]);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				customComponent: (
					<ChangeRequestsTableHeader
						defaultSwitchValue={defaultSwitchValue}
						checked={checked}
						handleSwitchChange={handleSwitchChange}
						showPositionsSelector={showPositionsSelector}
						selectedPositions={selectedPositions}
						positions={positions}
						handlePositionsChange={handlePositionsChange}
					/>
				),
				filter: {
					hide: !(!allowDeletion || !selectedRows.length),
				},
				search: {
					hide: !(!allowDeletion || !selectedRows.length),
					useQuickFilter: true,
				},
				delete: {
					deleteTitle: "Remove",
					hide: !allowDeletion || !selectedRows.length,
					disabled: deleting,
					onDeleteAction: handleDeleteAction,
				},
			},
			noRowsOverlay: {
				title: "No Change Request Data",
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: "No Change Request Data",
				className: "!text-montserrat",
			},
		}),
		[checked, deleting, showPositionsSelector, handleDeleteAction, selectedPositions, positions, !allowDeletion || !selectedRows.length]
	);

	const getRowClassName = useCallback(
		(params: GridRowParams) =>
			clsx({
				"skelton-row": params.row.__isShowingSkeleton,
				"!bg-table-hover": selectedRowId === params.row._id,
			}),
		[selectedRowId]
	);

	const isRowSelectableWrapper = useCallback(
		(params: GridRowParams) => isRowSelectable(params.row as IGetChangeRequest),
		[isRowSelectable]
	);

	if (isLoading) {
		return <TableLoader content rows={3} multiTitles={5} />;
	}

	return (
		<DataSmartTable
			// Data
			columns={columns as GridColDef[]}
			rows={augmentedData ?? []}
			// Models
			filterModel={filterModel}
			onFilterModelChange={setFilterModel}
			rowSelectionModel={selectedRows}
			onRowSelectionModelChange={setSelectedRows}
			onRowClick={handleRowClick}
			// Configurations
			columnVisibilityModel={columnVisibilityModel}
			customProps={customProps}
			disableColumnMenu
			disableRowSelectionOnClick
			headerHeight={30}
			hideFooter
			initialState={initialState}
			rowHeight={25}
			slotProps={slotProps}
			slots={slots}
			checkboxSelection={allowDeletion}
			getRowClassName={getRowClassName}
			isRowSelectable={isRowSelectableWrapper}
		/>
	);
};

export default ChangeRequestTable;
